import type { AddendumChange } from '@/lib/schemas/db'

/**
 * The service fields an addendum can amend, with the labels shown in the
 * change summary and audit trail.
 *
 * Kept in one place so the amend form, the summary modal and the stored
 * `label` on each change never drift apart. The Omni invoice number is here
 * deliberately — correcting it after invoicing is the common case.
 */
export const ADDENDUM_FIELDS = [
  { field: 'issueSummary', label: 'Issue' },
  // Job-only descriptive fields. Harmless for callouts and sessions: a field
  // absent from both the current values and the draft can never differ, so it
  // never produces a change.
  //
  // Deliberately excluded: the number tag (a unique index the stored record
  // owns, which an addendum does not write) and the device password (stored
  // encrypted). Both need a real record write, so they stay unamendable.
  { field: 'fault', label: 'Fault' },
  { field: 'deviceType', label: 'Device type' },
  { field: 'deviceDescription', label: 'Device(s) description' },
  { field: 'peripherals', label: 'Peripherals' },
  { field: 'contactAddress', label: 'Visit address' },
  { field: 'contactName', label: 'Site contact' },
  { field: 'contactPhone', label: 'Phone' },
  { field: 'contactEmail', label: 'Email' },
  { field: 'invoiceName', label: 'Invoice to' },
  { field: 'invoiceEmail', label: 'Invoice email' },
  { field: 'invoicePhone', label: 'Invoice phone' },
  { field: 'invoiceAddress', label: 'Invoice address' },
  { field: 'invoiceVat', label: 'VAT number' },
  { field: 'invoiceId', label: 'Invoice number (Omni)' },
] as const

export type AddendumFieldKey = (typeof ADDENDUM_FIELDS)[number]['field']

export function addendumFieldLabel(field: string): string {
  return ADDENDUM_FIELDS.find((entry) => entry.field === field)?.label ?? field
}

/**
 * Diff the amend form against the values currently in force, dropping
 * everything that did not actually change.
 */
export function buildAddendumChanges(
  current: Record<string, string | undefined>,
  draft: Record<string, string | undefined>,
): AddendumChange[] {
  return ADDENDUM_FIELDS.flatMap((entry) => {
    const before = current[entry.field] ?? ''
    const after = draft[entry.field] ?? ''
    if (before === after) return []
    return [{ field: entry.field, label: entry.label, before, after }]
  })
}
